import { message } from "antd";
import axios from "axios";
import { UseApiCallV2 } from "hooks/use-api-call-v2";
import { UseLazyApiCallV2 } from "hooks/use-lazy-api-call-v2";
import { useCurrentUserContext } from "providers";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { excelUrlRecoil } from "store/atom";
import * as XLSX from "xlsx";

export const UseExcelFile = ({
  setIsExcelFileChanged,
  hotRef,
  refetchFieldHistory,
}) => {
  // const [excelUrlState, setExcelUrlState] = useRecoilState(excelUrlRecoil);

  const { user } = useCurrentUserContext();

  const [loadingExcel, setLoadingExcel] = useState(false);
  const { caseId } = useParams();
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const initialDataRef = useRef(null);
  const [data, setData] = useState([]);
  const [mergedCells, setMergedCells] = useState([]);
  // const hotRef = useRef(null);
  const [saveExcelFile, { isLoading: excelFileLoading }] = UseLazyApiCallV2({
    url: "/save_xlsx",
    method: "POST",
  });
  const [saveExcelHistory, { isLoading: saveExcelHistoryLoading }] =
    UseLazyApiCallV2({
      url: "/xlsx_history",
      method: "POST",
    });

  const [
    getExcelUrl,
    { data: excelUrl, isLoading: excelUrlLoading, refetch: refetchExcelUrl },
  ] = UseLazyApiCallV2({
    url: "/get_xlsx",
    method: "POST",
  });

  const normalizeData = (dataArray) =>
    dataArray.map((row) =>
      row.map((cell) => (cell === null || cell === undefined ? "" : cell)),
    );

  async function loadExcel() {
    setLoadingExcel(true);

    const excelUrlState = await getExcelUrl({
      params: {
        case_id: caseId,
      },
    });

    try {
      const response = await fetch(excelUrlState?.url);

      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Convert sheet to a 2D array preserving empty cells
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      const parsedData = [];
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const row = [];
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = { r: R, c: C };
          const cellRef = XLSX.utils.encode_cell(cellAddress);
          const cell = worksheet[cellRef];
          row.push(cell ? cell.v : "");
        }
        parsedData.push(row);
      }

      // Handle merged cells
      const merges = worksheet["!merges"] || [];
      const htMerges = merges.map((merge) => ({
        row: merge.s.r,
        col: merge.s.c,
        rowspan: merge.e.r - merge.s.r + 1,
        colspan: merge.e.c - merge.s.c + 1,
      }));

      setData(parsedData);
      // setMergedCells(htMerges);
      setMergedCells([
        {
          row: 0,
          col: 0,
          rowspan: 1,
          colspan: parsedData[0].length,
        },
      ]);
      // 🔥 Save initial snapshot AFTER data is prepared
      initialDataRef.current = JSON.stringify(normalizeData(parsedData));
      setIsExcelFileChanged(false);
    } catch (error) {
      console.log({ error });
    } finally {
      setLoadingExcel(false);
    }
  }
  useEffect(() => {
    loadExcel();
  }, []);

  // Export updated Excel
  const handleExport = async (action) => {
    const hotInstance = hotRef.current.hotInstance;
    const updatedData = hotInstance.getData();

    const worksheet = XLSX.utils.aoa_to_sheet(updatedData);

    // Add merged cells back to worksheet
    if (mergedCells.length) {
      worksheet["!merges"] = mergedCells.map((m) => ({
        s: { r: m.row, c: m.col },
        e: { r: m.row + m.rowspan - 1, c: m.col + m.colspan - 1 },
      }));
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    if (action === "save") {
      const res = await saveExcelFile({
        params: {
          case_id: caseId,
        },
      });

      if (res?.url) {
        setUploadingExcel(true);
        try {
          const response = await axios.put(res.url, excelBuffer, {
            headers: {
              "Content-Type":
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            },
          });
          if (response?.status === 200) {
            message.success("Updated Successfully");
            const currentData = hotRef.current.hotInstance.getData();
            initialDataRef.current = JSON.stringify(currentData);
            setIsExcelFileChanged(false);

            const historyUpdateRes = await saveExcelHistory({
              params: {
                loggedUserName: `${user?.firstName} ${user?.lastName}`,
                loggedUserEmail: user?.email,
                case_id: caseId,
                fieldName: "invoiceAmountSmile",
              },
            });
            if (historyUpdateRes) {
              await refetchFieldHistory();
              loadExcel();
            }
          }
        } catch (error) {
        } finally {
          setUploadingExcel(false);
        }
      }
    } else {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "updated.xlsx";
      link.click();
    }
  };
  return {
    handleExport,
    excelFileLoading,
    uploadingExcel,
    loadingExcel,
    hotRef,
    initialDataRef,
    normalizeData,
    data,
    mergedCells,
    loadExcel,
  };
};
